// src/Dashboard/admin/layout/Footer.jsx
import { Link } from "react-router-dom";
import { LifeBuoy, Shield, ExternalLink } from "lucide-react";

export default function Footer({
  appName = "VOP-LMS",
  scopeChips = [],
  onOpenSupport,
  onOpenPrivacy,
  onOpenTerms,
  version, // e.g. "v1.0.0"
}) {
  const year = new Date().getFullYear();

  const chips = Array.isArray(scopeChips) ? scopeChips.filter(Boolean) : [];
  const shortChips = chips.slice(0, 3);

  return (
    <footer className="mt-10 border-t border-orange-100 dark:border-slate-800 bg-white/90 dark:bg-slate-950/90 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          {/* Left */}
          <div className="min-w-0">
            <div className="text-sm font-extrabold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
              {appName} Admin
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-slate-300">
              <span className="font-semibold text-gray-700 dark:text-slate-200">Scope:</span>

              {shortChips.length ? (
                <>
                  {shortChips.map((c, idx) => (
                    <span
                      key={`${c}-${idx}`}
                      className="px-2.5 py-1 rounded-xl bg-orange-50 dark:bg-slate-900 border border-orange-100 dark:border-slate-800 text-[11px] font-semibold text-orange-700 dark:text-slate-100"
                      title={c}
                    >
                      {c}
                    </span>
                  ))}
                  {chips.length > shortChips.length ? (
                    <span className="text-[11px] text-gray-500 dark:text-slate-400">
                      +{chips.length - shortChips.length} more
                    </span>
                  ) : null}
                </>
              ) : (
                <span className="px-2.5 py-1 rounded-xl bg-orange-50 dark:bg-slate-900 border border-orange-100 dark:border-slate-800 text-[11px] font-semibold text-orange-700 dark:text-slate-100">
                  Union (all)
                </span>
              )}
            </div>
          </div>

          {/* Middle */}
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/"
              className="px-3 py-2 rounded-xl text-xs font-semibold border border-orange-100 dark:border-slate-800 bg-orange-50 dark:bg-slate-900 text-orange-700 dark:text-slate-100 hover:shadow-sm transition"
            >
              Go to VOP
            </Link>

            <Link
              to="/courses"
              className="px-3 py-2 rounded-xl text-xs font-semibold border border-orange-100 dark:border-slate-800 bg-orange-50 dark:bg-slate-900 text-orange-700 dark:text-slate-100 hover:shadow-sm transition"
            >
              Courses
            </Link>

            {onOpenSupport ? (
              <button
                type="button"
                onClick={onOpenSupport}
                className="px-3 py-2 rounded-xl text-xs font-semibold border border-orange-100 dark:border-slate-800 bg-orange-50 dark:bg-slate-900 text-orange-700 dark:text-slate-100 hover:shadow-sm transition inline-flex items-center gap-2"
              >
                <LifeBuoy size={14} />
                Support
              </button>
            ) : (
              <Link
                to="/support"
                className="px-3 py-2 rounded-xl text-xs font-semibold border border-orange-100 dark:border-slate-800 bg-orange-50 dark:bg-slate-900 text-orange-700 dark:text-slate-100 hover:shadow-sm transition inline-flex items-center gap-2"
              >
                <LifeBuoy size={14} />
                Support
              </Link>
            )}

            {onOpenPrivacy ? (
              <button
                type="button"
                onClick={onOpenPrivacy}
                className="px-3 py-2 rounded-xl text-xs font-semibold border border-orange-100 dark:border-slate-800 bg-orange-50 dark:bg-slate-900 text-orange-700 dark:text-slate-100 hover:shadow-sm transition inline-flex items-center gap-2"
              >
                <Shield size={14} />
                Privacy
              </button>
            ) : (
              <Link
                to="/privacy"
                className="px-3 py-2 rounded-xl text-xs font-semibold border border-orange-100 dark:border-slate-800 bg-orange-50 dark:bg-slate-900 text-orange-700 dark:text-slate-100 hover:shadow-sm transition inline-flex items-center gap-2"
              >
                <Shield size={14} />
                Privacy
              </Link>
            )}

            {onOpenTerms ? (
              <button
                type="button"
                onClick={onOpenTerms}
                className="px-3 py-2 rounded-xl text-xs font-semibold border border-orange-100 dark:border-slate-800 bg-orange-50 dark:bg-slate-900 text-orange-700 dark:text-slate-100 hover:shadow-sm transition inline-flex items-center gap-2"
              >
                <ExternalLink size={14} />
                Terms
              </button>
            ) : (
              <Link
                to="/terms"
                className="px-3 py-2 rounded-xl text-xs font-semibold border border-orange-100 dark:border-slate-800 bg-orange-50 dark:bg-slate-900 text-orange-700 dark:text-slate-100 hover:shadow-sm transition inline-flex items-center gap-2"
              >
                <ExternalLink size={14} />
                Terms
              </Link>
            )}
          </div>

          {/* Right */}
          <div className="text-xs text-gray-500 dark:text-slate-400 flex flex-wrap items-center gap-3 md:justify-end">
            <span>© {year} {appName}</span>
            <span className="text-gray-300 dark:text-slate-700">•</span>
            <span>
              Version:{" "}
              <span className="font-semibold">
                {version ? version : appName}
              </span>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
